import { Injectable } from '@angular/core';
import { FtError, SharedConstants } from '@formbird/types';
import {
  SearchResultsProcessor,
  SharedUrlRoutes,
  UtilArray,
  UtilError,
  UtilHttpStatus,
  UtilSequenceProcess,
  UtilType
} from '@formbird/shared';
import { HttpClient } from '@angular/common/http';
import { LoggedInUserService } from '../user/logged-in-user.service';
import { ConfigService } from '../config/config.service';
import { OfflineStatusService } from '../offline-status/offline-status.service';
import { IndexedDBService } from '../indexeddb/indexed-db.service';
import { DocumentSessionService } from '../document/document-session.service';

const serverRoutes = SharedUrlRoutes.serverRoutes;

const logger = console;

@Injectable({
  providedIn: 'root'
})
export class RestDataService {

  constructor(
    private http: HttpClient,
    private loggedInUserService: LoggedInUserService,
    private configService: ConfigService,
    private offlineStatusService: OfflineStatusService,
    private indexedDBService: IndexedDBService,
    private documentSessionService: DocumentSessionService

  ) {
  }

  public checkDocumentExists(url, id) {

    const _self = this;

    return new Promise((resolve, reject) => {
      let fullUrl = url;
      if (id) {
        fullUrl += '/' + id;
      }

      _self.http.get(fullUrl).subscribe(
        res => {
          resolve(res);
        },
        err => {
          const msg = err ? err.message : 'Something went wrong when checking document exists.';
          const error = new FtError(msg);
          if (err) {
            error.status = err.status;
            error.statusText = err.statusText;
            error.message = err.message;
          }

          reject(error);
        }
      );
    });
  }

  public getDocument(url, id) {

    const _self = this;

    return new Promise((resolve, reject) => {

      let fullUrl = url;

      if (id) {
        fullUrl += '/' + id;
      }

      _self.http.get(fullUrl).subscribe(
        res => {
          resolve(res);
        },
        err => {

          const msg = err ? err.message : 'Something went wrong when getting document.';
          const error = new FtError(msg);

          if (err) {
            error.status = err.status;
            error.statusText = err.statusText;
            error.message = err.error;
          }

          reject(error);
        }
      );
    });
  }

  public loadDataCache(syncStartTime, isInitialize) {

    const _self = this;

    return new Promise((resolve, reject) => {

      _self.getCachedDocumentCount(syncStartTime).subscribe(
        res => {

          const data: any = res;

          logger.info('Loading data cache. With info: ', data);

          _self.offlineStatusService.addToMaxCacheCount(data.docCount);

          function loadEach(page) {
            return _self.loadCachedDocuments(syncStartTime, page, isInitialize);
          }

          UtilSequenceProcess.processRange(1, data.pages, loadEach).then(
            function allPromiseSuccessFunc(result) {

              resolve(result);

            },
            function errorFunc(err) {
              reject(err);
            }
          );

        },
        err => {

          const msg = 'Error requesting offline document count. Status: ' + err.status;
          logger.error(err.message);

          reject(new FtError(msg));

        }
      );
    });
  }

  private processCachingDocuments(documents, isInitialize?, updateOfflineStatus?) {

    const _self = this;

    return new Promise((resolve, reject) => {

      const promises = [];
      if (isInitialize === true){
        promises.push(_self.indexedDBService.doCacheBulkDocuments(documents, updateOfflineStatus));

      }else {
        for (let i = 0; i < documents.length; i++) {

          const document = documents[i];
          if (isInitialize === false) {
            promises.push(_self.indexedDBService.cachePushedDocument(document, [document], updateOfflineStatus));
          } else {
            promises.push(_self.indexedDBService.doCacheDocument(document, updateOfflineStatus));
          }
        }

      }

      Promise.all(promises).then(
        function successFunction(results) {
          resolve(results);
        },
        function errorFunction(err) {
          reject(new Error(err.message));
        }
      );

    });
  }
  public getIndexedDBIndexes() {

    const options = {
      filter: {
        query: {
          'bool': {
            'must': [
              {
                'exists': {
                  'field': 'indexedDBIndexes'
                }
              },
              {
                'term': {
                  'systemHeader.systemType': 'template'
                }
              }
            ]
          }
        }
      },
      sourceFilter: 'indexedDBIndexes'
    };

    return this.search(options).then(
      function(results: any) {

        const indexedDBIndexes = [];

        if (results.data.hits.total) {

          for (let i = 0; i < results.data.hits.hits.length; i++) {

            const template = results.data.hits.hits[i]._source;

            const indexes = template.indexedDBIndexes;

            if (indexes) {

              for (let j = 0; j < indexes.length; j++) {

                UtilArray.add(indexedDBIndexes, indexes[j], true);
              }
            }
          }
        }

        return Promise.resolve(indexedDBIndexes);

      },
      function(error) {

        return Promise.reject(error);
      }
    );
  }

  private getCachedDocumentCount(syncStartTime) {

    const fullUrl = serverRoutes.syncDocumentsCountURL + '/' + syncStartTime;

    return this.http.get(fullUrl);

  }

  private loadCachedDocuments(syncStartTime, pageNo, isInitialize) {

    logger.info('Loading cached documents for page: ' + pageNo);

    const _self = this;

    const fullUrl = serverRoutes.syncDocuments + '/' + syncStartTime + '/' + pageNo;

    // ensures request is processed only when ui turned idle
    this.offlineStatusService.pageDownloadProgress.pageNumber = pageNo;

    const getParams: any = {
      // Them TT check request progress
      eventHandlers: {
        progress: function (e) {
          if (e.lengthComputable) {
            _self.offlineStatusService.pageDownloadProgress.loaded = e.loaded;
            _self.offlineStatusService.pageDownloadProgress.total = e.total;
          }
        }
      }
    };

    return new Promise((resolve, reject) => {
      _self.http.get(fullUrl).subscribe(
        res => {
          const data: any = res;
          const templates = data.templates;
          const documents = data.documents;

          // Cache templates first to register indexes, no need to update offline count.
          // It will be set in the full list of offline documents in the next step
          _self.processCachingDocuments(templates, isInitialize, false).then(
            function cachedOk() {
              // Then caching document to make sure the corresponding template is ready to use
              _self.processCachingDocuments(documents, isInitialize).then(
                function successFunction(results) {
                  resolve(results);
                },
                function errorFunction(err) {
                  reject(new Error(err.message));
                }
              );
            },
            function cachedFailed(err) {
              logger.warn('Some templates could not be cached successfully: ' + err);

              _self.processCachingDocuments(documents, isInitialize).then(
                function successFunction(results) {
                  resolve(results);
                },
                function errorFunction(error) {
                  reject(new Error(error.message));
                }
              );
            });
        },
        err => {

          const msg = 'Error caching data. Network data will be used: ' + err.status;
          logger.error(msg);
          reject(err);

        }
      );
    });
  }

  // get full rest url from a url without the host and webapp section, so a url of 'cases' will become
  // {hostname}:{port}/{webappname}/cases. eg localhost:8090/focus-rest/cases
  private getFullRestURL(url, id?) {
    let fullUrl = url;

    if (UtilType.hasValue(id)) {
      fullUrl += '/' + id;
    }

    return fullUrl;
  }

  /**
   * As per 10539, we send the new document directly to the server and let it run jsonPatch.
   * For simplicity, other parameters from the old deep Update was
   * removed because the server can resolve them with just the newDocument value.
   *
   * @param newDocument the new document to update
   * @returns promise|*|jQuery.promise the promise
   */
  public jsonPatchUpdate(newDocument, options) {

    const _self = this;

    const updateUrl = SharedUrlRoutes.serverRoutes.documentDeepDiffUpdate + '/' + newDocument.documentId;

    return new Promise((resolve, reject) => {
      const putData = {
        document: newDocument,
        options: options
      };

      _self.http.put(updateUrl, putData)
        .subscribe(
          res => {
            resolve(res);
          },

          err => {
            console.log(err);
            const error = err ? new Error(err.error) : new Error('Something went wrong when updating document.');
            reject(error);

          });
    });

  }

  /*
   insert function
   parameters:
   bean - the bean to insert
   url - the url for the restful web service. This will also be used as a key in the database
   successFunction - a function to execute on success to do post save steps
   cachingRequired - whether to save the record to the local database. Records that never need
   to be loaded in the app on their own, such as events, can have this set to false
   */
  public insert(bean, url) {

    const _self = this;

    return new Promise((resolve, reject) => {

      const fullUrl = _self.getFullRestURL(url, bean.id);
      const options = {headers: {
        options: JSON.stringify({
          documentSession: this.documentSessionService.getDocumentSession(bean.documentId)
        })
      }};
      _self.http.post(fullUrl, bean, options).subscribe(
        res => {
          resolve(res);
        },

        err => {
          console.log(err);
          const error = err ? new Error(err.error) : new Error('Something went wrong when inserting document.');
          reject(error);

        }
      );
    });
  }

  /*
   destroy function
   parameters:
   id - the id of the bean to delete
   url - the url for the restful web service
   cachingRequired - whether to save the record to the local database. Records that never need
   templateId - template used when deleting document
   versionId - version of document during deletion
   to be loaded in the app on their own, such as events, can have this set to false
   */
  public destroy(id, url, cachingRequired, templateId, previousVersionId) {

    const _self = this;

    return new Promise((resolve, reject) => {

      const fullUrl = _self.getFullRestURL(url, id) + '/' + templateId + '/' + previousVersionId;

      _self.http.delete(fullUrl).subscribe(
        res => {
          resolve(res);
        },

        err => {
          console.log(err);
          const error = err ? new Error(err.error) : new Error('Something went wrong when destroying document.');
          reject(error);

        }
      );

    });

  }

  public search(options) {

    logger.info('Doing search in RestDataService');

    const _self = this;

    return new Promise((resolve, reject) => {

      _self.http.post('api/documentSearch', options, {headers: { 'Content-Type': 'application/json' }}).subscribe(
        res => {
          resolve(res);
        },

        err => {
          let msg;
          if (err && err.message) {

            msg = 'Searching document failed with error: ';
            msg += err.message;

          } else {

            msg = 'Something went wrong when searching document. Status: ' + err.status;

          }

          if (err && err.status === UtilHttpStatus.SERVER_UNRESPONSIVE) {

            reject(UtilError.createError(msg, UtilError.SERVER_UNRESPONSE_ERROR));

          } else {

            reject(new Error(msg));

          }
        }
      );
    });
  }

  public loadVersions(versionIds): Promise<any> {

    const _self = this;

    return new Promise((resolve, reject) => {

      if (!versionIds || !versionIds.length) {
        resolve();
      } else {

        const url = SharedUrlRoutes.serverRoutes.documentSearch;
        const searchProviderName = _self.configService.clientConfig().defaultSearchProvider;
        let options;

        if (searchProviderName === SharedConstants.SEARCH_PROVIDERS.COUCHBASE) {

          let statement = 'SELECT * FROM <bucketName> WHERE ';
          statement += 'systemHeader.versionId in ' + JSON.stringify(versionIds);
          options = {
            filter: statement,
            'includeAllVersions': true
          };

        } else {

          options = {
            filter: {
              query: {
                'bool': {
                  'must': [
                    { 'terms': { 'systemHeader.versionId': versionIds } }
                  ]
                }
              }
            },
            'includeAllVersions': true
          };
        }

        _self.http.post(url, options).subscribe(
          res => {

            SearchResultsProcessor.processSearchResults(res,
              function elementProcessing(doc) {
              },
              function complete(docs) {
                resolve(docs);
              }
            );
          },

          err => {

            const msg = 'Error requesting versions. Status: ' + err.status;

            logger.error(err.message);

            reject(new Error(msg));
          }
        );
      }

    });

  }

  /*
   undestroy function
   parameters:
   id - the id of the bean to undelete
   url - the url for the restful web service
   */
  public undestroy(id, url) {

    const _self = this;

    const fullUrl = this.getFullRestURL(url, id);

    return new Promise((resolve, reject) => {

      _self.http.put(fullUrl, null).subscribe(
        res => {

          resolve(res);

        },
        err => {

          const error = err ? new Error(err.error) : new Error('Something went wrong when undestroying document.');
          reject(error);

        }
      );
    });
  }

  /*
  bulkUploadDocuments function
  parameters:
  documents - the array of documents to upload.
  url - the url for the restful web service
  */
  public bulkUploadDocuments(documents, url) {

    const _self = this;

    return new Promise((resolve, reject) => {
      const fullUrl = _self.getFullRestURL(url);

      _self.http.post(fullUrl, documents).subscribe(
        res => {
          resolve(res);
        },

        err => {
          const error = err ? new Error(err) : new Error('Something went wrong when uploading documents.');
          reject(error);
        }
      );

    });

  }
}
